import React, { useEffect, useRef } from "react";
import { classNames } from "primereact/utils";
import { InputField, InputFieldProps } from "./InputField";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import { FloatLabel } from "primereact/floatlabel";
import { Tooltip } from "primereact/tooltip";
import { useFormContext, useFormState } from "react-hook-form";
import { resolveFieldError } from "./resolveFieldError";
import "./LabelInputField.scss";

export interface LabelInputFieldProps extends InputFieldProps {
    float?: boolean;
    label?: string;
    containerClassName?: string;
    automaticGrid?: boolean;
    pageFilter?: boolean;
    /** Wide search field with icon (SpoofyCrud filters). */
    filterSearch?: boolean;
    centered?: boolean;
    labelClassName?: string;
    inputClassName?: string;
    disable?: boolean;
    tooltip?: boolean;
    tooltipComponent?: React.ReactNode;
    tooltipText?: string;
    tooltipIcon?: string;
    type?: string;
    /** Uncontrolled / non-form usage: pass PrimeReact component + props directly. */
    componentProps?: Record<string, any>;
    [key: string]: any;
}

const LabelInputFieldStandalone: React.FC<LabelInputFieldProps> = ({
    label,
    labelClassName,
    className,
    containerClassName,
    component,
    componentProps = {},
    inputClassName,
    name,
}) => {
    if (!component) {
        return null;
    }
    const Component = component;
    const inputId = componentProps.id ?? componentProps.inputId ?? name;

    return (
        <div className={classNames("lovable-label-input-field", containerClassName, className)}>
            {label ? (
                <label
                    className={classNames("lovable-label", labelClassName)}
                    htmlFor={inputId}
                >
                    {label}
                </label>
            ) : null}
            <Component
                {...componentProps}
                id={inputId}
                className={classNames("w-full", componentProps.className, inputClassName)}
            />
        </div>
    );
};

const LabelInputFieldForm: React.FC<LabelInputFieldProps> = ({
    float,
    label,
    automaticGrid = false,
    centered = false,
    pageFilter = false,
    filterSearch = false,
    containerClassName,
    inputClassName,
    labelClassName,
    name,
    component,
    rules,
    disable,
    tooltip = false,
    tooltipComponent,
    tooltipText,
    tooltipIcon,
    type,
    placeholder,
    className,
    componentProps = {},
    ...props
}) => {
    const isToggleFilter =
        pageFilter && (component === Checkbox || component === InputSwitch);

    const resolvedPlaceholder =
        placeholder ?? (pageFilter && label && !isToggleFilter ? String(label) : undefined);

    const { control } = useFormContext();
    const { errors } = useFormState({ control, name });
    const error = resolveFieldError(errors, name);
    const tooltipRef = useRef(null);
    const tooltipTargetClass = name ? `tooltip-${name}` : undefined;

    if (!control) {
        throw new Error("LabelInputField with name must be used within a FormProvider");
    }

    useEffect(() => {
        if (tooltipRef.current) {
            (tooltipRef.current as any).updateTargetEvents();
        }
    }, [error]);

    const finalContainerClassName = classNames(
        "lovable-label-input-field",
        pageFilter ? containerClassName ?? "col-12 sm:col-12 md:col-6 lg:col-4 xl:col-3 grid pr-3" : containerClassName || "",
        filterSearch && "spoofy-filter-field--search",
        className,
        error && "lovable-label-input-field--invalid",
        {
            "centered-container": centered,
            "automatic-grid-container": automaticGrid && !centered,
            "col-6 grid grid-nogutter": automaticGrid && !centered && !containerClassName,
        }
    );

    const usesSrOnlyLabel = labelClassName?.includes("spoofy-filter-field__label");

    const finalLabelClassName = classNames(
        usesSrOnlyLabel ? labelClassName : classNames("lovable-label", labelClassName),
        pageFilter && !usesSrOnlyLabel && !labelClassName ? "col-5" : null,
        isToggleFilter && "spoofy-filter-field__label spoofy-filter-field__label--visible",
        {
            "centered-label flex justify-content-center": centered,
            "automatic-grid-label align-self-center": automaticGrid && !centered,
            "col-4 grid grid-nogutter": automaticGrid && !centered && !labelClassName,
            "flex align-self-center": (pageFilter || automaticGrid) && !centered && !isToggleFilter,
        }
    );

    const finalInputClassName = classNames(
        pageFilter ? inputClassName ?? "col-7" : inputClassName || "",
        {
            "centered-input": centered,
            "automatic-grid-input": automaticGrid && !centered,
            "col-8 grid grid-nogutter": automaticGrid && !centered && !inputClassName,
        }
    );

    const renderTooltip = () => {
        if (tooltipComponent) {
            return tooltipComponent;
        }
        if (tooltipText && tooltipTargetClass) {
            return (
                <>
                    <Tooltip target={`.${tooltipTargetClass}`} />
                    <i
                        className={`${tooltipTargetClass} ${tooltipIcon} tooltipInput`}
                        data-pr-tooltip={tooltipText}
                        style={{ fontSize: "1rem" }}
                    />
                </>
            );
        }
        return null;
    };

    const renderFormControl = () => (
        <InputField
            key={name ? `input:${name}` : undefined}
            className={finalInputClassName}
            type={type}
            name={name!}
            component={component}
            disable={disable}
            rules={rules}
            centered={centered}
            automaticGrid={automaticGrid}
            tooltip={tooltip}
            pageFilter={pageFilter}
            placeholder={resolvedPlaceholder}
            {...componentProps}
            {...props}
        />
    );

    const generateInput = () => {
        if (float) {
            return (
                <FloatLabel>
                    {renderFormControl()}
                    <label key={name ? `label:${name}` : undefined} htmlFor={name}>{label}</label>
                    {renderTooltip()}
                </FloatLabel>
            );
        }

		return (
			<>
				{label && !isToggleFilter ? (
					<label
						key={name ? `label:${name}` : undefined}
						className={finalLabelClassName}
						htmlFor={name}
					>
						<span className="lovable-label__text">{label}</span>
						{renderTooltip()}
					</label>
				) : null}
				{renderFormControl()}
			</>
		);
    };

    const content = generateInput();

    const errorMessage = error?.message?.toString();

    if (finalContainerClassName) {
        return (
            <div key={name ? `container:${name}` : undefined} className={finalContainerClassName || undefined}>
                {content}
                {!pageFilter && errorMessage ? (
                    <span className="lovable-label-input-field__error" role="alert">
                        {errorMessage}
                    </span>
                ) : null}
            </div>
        );
    }

    return (
        <>
            {content}
            {!pageFilter && errorMessage ? (
                <span className="lovable-label-input-field__error" role="alert">
                    {errorMessage}
                </span>
            ) : null}
        </>
    );
};

const LabelInputField: React.FC<LabelInputFieldProps> = (props) => {
    if (props.name) {
        return <LabelInputFieldForm {...props} />;
    }
    return <LabelInputFieldStandalone {...props} />;
};

export default LabelInputField;
export { LabelInputField };
